import { ApiError, apiClient } from './apiClient';

export type UserRole = 'User' | 'Manager' | 'Admin';

export type SigninDesign = 'random' | 'split-panel' | 'photogenic';

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  preferredLocale: string | null;
  signinDesign: SigninDesign | null;
  /** Slug of the user's chosen theme (null = use the org default). */
  preferredThemeSlug: string | null;
  photoUrl: string | null;
  /** Aspire ContactID of the crew leader this user leads as (null = unmapped). */
  aspireCrewLeaderContactId: number | null;
}

export type ProfileResult =
  | { status: 'authorized'; profile: UserProfile }
  | { status: 'access_denied'; message: string }
  | { status: 'unauthorized' };

export async function loadProfile(): Promise<ProfileResult> {
  try {
    const profile = await apiClient.get<UserProfile>('/api/auth/me');
    return { status: 'authorized', profile };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 403 && err.code === 'access_denied') {
        return { status: 'access_denied', message: err.message };
      }
      if (err.status === 401) {
        return { status: 'unauthorized' };
      }
    }
    throw err;
  }
}

export function hasPermission(role: UserRole | null, required: UserRole): boolean {
  if (!role) return false;
  const rank: Record<UserRole, number> = { User: 1, Manager: 2, Admin: 3 };
  return rank[role] >= rank[required];
}

export function isAdmin(role: UserRole | null): boolean {
  return role === 'Admin';
}

export function isManager(role: UserRole | null): boolean {
  return hasPermission(role, 'Manager');
}

export function isUser(role: UserRole | null): boolean {
  return hasPermission(role, 'User');
}

/**
 * Convenience wrapper around `loadProfile()` that returns the profile object on
 * success and null otherwise. Use when callers don't need to distinguish
 * between unauthorized / access_denied / network errors.
 */
export async function getUserProfile(): Promise<UserProfile | null> {
  try {
    const result = await loadProfile();
    return result.status === 'authorized' ? result.profile : null;
  } catch {
    return null;
  }
}
